import { Container, ContainerPadding, Text, TokenTextAppearance } from '@volkswagen-onehub/components-core';
import Axios, { AxiosResponse } from 'axios';
import React, { useEffect } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { CarlineSelect, Loading, Nombre, Telefono } from 'src/components';
import { FormController, OneElementTemplate, TwoElementsTemplate, useOneFormContext } from 'src/feature-app';
import { getParamsForm } from 'src/feature-app/get-params-form';
import { useIsComerciales } from 'src/feature-app/hooks';
import { FormDataGroup, FormInfo, MolecularResponse, OneFormState, Steps } from 'src/types';
import { CallMeConfirmacion, CallMeThankYou } from '.';
import { useInitializeCallmeForm } from './use-initialize-callme-form';

export function CallMeForm() {
	const { initializeForm } = useOneFormContext();
	const store = useStore();
	const dispatch = useDispatch();
	const isComerciales = useIsComerciales();

	let index = 0;
	const defaultSteps: Steps[] = [
		{
			title: (
				<>
					<Text bold>¿Qué modelo </Text> te interesa?
				</>
			),
			fields: (
				<OneElementTemplate
					element={
						<div style={{ marginBottom: '2px' }}>
							<CarlineSelect useCarline={true} />
						</div>
					}
				/>
			),
			screenIndex: index++,
			name: 'Modelo',
			outputs: ['modelo'],
			outputsText: ['modelo'],
		},
		{
			title: (
				<Container wrap={'always'} padding={{ bottom: ContainerPadding.static600 }}>
					<Text>
						Para llamarte, necesitamos <Text bold>tus datos de contacto</Text>
					</Text>
					<Text appearance={TokenTextAppearance.copy200}>Nos pondremos en contacto contigo de lunes a viernes de 9:00 h a 19:30 h (excepto festivos nacionales) para darte más información.</Text>
				</Container>
			),
			fields: <TwoElementsTemplate firstElement={<Nombre />} secondElement={<Telefono hidePrefijo={isComerciales} paginaConfirmacion={false} required={true} />} />,
			screenIndex: index++,
			name: 'NombreTelefono',
			outputs: ['name', 'cellphone'],
			outputsText: ['name', 'cellphone'],
		},
		{
			fields: <CallMeConfirmacion />,
			screenIndex: index++,
			name: 'Confirmacion',
			isLastStep: true,
			hidePreviousStep: true,
		},
	];

	const NuevoFormData: Partial<FormDataGroup> = {
		headers: {
			DESC_BRAND: 'VW',
			DESC_VN_VO: 'VN',
			DESC_GDPR_ADAPTED: 'true',
		},
		fields: {
			formName: 'callme',
			formType: 'contact',
			pageCategory: 'Call Me',
			formularioFlag: 'callMe',
		}, // Analítica
	};

	const getFormInfo = (): {
		firstStep: Steps;
		lastStep: Steps;
		stepsHistory: number[];
		numberOfScreens: number;
		newSteps?: Steps[];
	} => {
		return {
			firstStep: steps[0],
			lastStep: steps[steps.length - 1],
			stepsHistory: [0],
			numberOfScreens: steps.length,
			newSteps: null,
		};
	};

	const sendForm = (): Promise<boolean> => {
		const { formData }: OneFormState = store.getState();
		const { fields } = formData;

		return new Promise<any>(async (resolve, reject) => {
			const { tc, today, referalUrl, url, device } = getParamsForm();

			const data = {
				...(window.location.hostname != 'www.volkswagen-comerciales.es' && { PRE: 1 }),
				name: fields.name,
				phone: `${fields.cellphone}`,
				DESC_LSSI: fields.lssi === 'PERMITIDO' ? 'S' : 'N',
				DESC_LOPD_GDPR_PERFILADO: fields.tmk === 'PERMITIDO' ? 'S' : 'N',
				ref: url,
				modelo: fields.modelo,
			};

			const response: AxiosResponse<MolecularResponse> = await Axios.post('https://www.volkswagencomerciales.es/call-me-now/email', data).catch((err) => {
				return null;
			});

			if (response?.data?.success && response?.status == 200) {
				dispatch({ type: 'SET_FORM_RESPONSE', payload: response.data });
				resolve(true);
			} else {
				resolve(false);
			}
		});
	};

	const { loading, steps, firstStep, lastStep } = useInitializeCallmeForm(defaultSteps);

	useEffect(() => {
		if (loading) {
			return;
		}
		const { stepsHistory, numberOfScreens, newSteps } = getFormInfo();

		const NuevoFormInfo: Partial<FormInfo> = {
			formStarted: true,
			sendFormCallback: sendForm,
			fullScreen: firstStep.fullScreen ? true : false,
			lastStep,
			numberOfScreens,
			formTheme: 'inverted',
			screenIndex: firstStep.screenIndex,
			step: firstStep,
			steps: newSteps ? newSteps : steps,
			notALayer: false,
			formType: 'layer',
			stepsHistory,
			thankYouComponent: <CallMeThankYou />,
		};

		initializeForm(NuevoFormInfo, NuevoFormData);
	}, [loading]);

	return loading ? <Loading /> : <FormController steps={steps} screenType="layer-screen" />;
}
